import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSocket } from '../context/SocketContext';
import PresetManager from '../components/PresetManager';

const TabCalculation = () => {
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [presets, setPresets] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [groupBy, setGroupBy] = useState('sos');
  const [sentTabs, setSentTabs] = useState(new Set());
  const [selectedTabId, setSelectedTabId] = useState(null);

  const { onTabConfirmed, onTabsRemoved } = useSocket();

  useEffect(() => {
    loadTabs();
  }, []);

  useEffect(() => {
    const unsubscribeTabConfirmed = onTabConfirmed((data) => {
      if (data && typeof data === 'object' && data.sosId) {
        setMessage(`Tab für SOS ${data.sosId} wurde bestätigt`);
        setMessageType('success');
        loadTabs();
      }
    });

    const unsubscribeTabsRemoved = onTabsRemoved((data) => {
      if (data && typeof data === 'object' && data.sosId) {
        setMessage(`Alle Tabs für SOS ${data.sosId} wurden entfernt`);
        setMessageType('info');
        loadTabs();
      }
    });

    return () => {
      unsubscribeTabConfirmed();
      unsubscribeTabsRemoved();
    };
  }, [onTabConfirmed, onTabsRemoved]);

  const loadTabs = async () => {
    setLoading(true);
    try {
      console.log('🔄 Loading tabs...');
      const response = await axios.get('/api/tabs');
      console.log('📥 Tabs Response:', response.data);
      
      if (response.data?.data?.tabs && Array.isArray(response.data.data.tabs)) {
        console.log('✅ Setting tabs:', response.data.data.tabs.length, 'entries');
        setTabs(response.data.data.tabs);
      } else {
        console.log('⚠️ No tabs found in response');
        setTabs([]);
      }
    } catch (error) {
      console.error('❌ Fehler beim Laden der Tabs:', error);
      setMessage('Fehler beim Laden der Tabs');
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculateAll = async () => {
    if (!selectedPreset) {
      setMessage('Bitte wählen Sie zuerst ein Preset aus');
      setMessageType('error');
      return;
    }

    setCalculating(true);
    setMessage('');

    try {
      console.log('🎯 Calculating tabs for preset:', selectedPreset._id, selectedPreset.name);
      const response = await axios.post('/api/tabs/calculate', {
        presetId: selectedPreset._id
      });
      console.log('📊 Tab calculation response:', response.data);
      
      if (response.data?.data?.tabs) {
        setTabs(response.data.data.tabs);
        setMessage(`${response.data.data.tabs.length} Tabs für Preset "${selectedPreset.name}" erfolgreich berechnet`);
        setMessageType('success');
      } else {
        setMessage(response.data?.message || 'Tabs berechnet');
        setMessageType('success');
      }
      
      console.log('🔄 Reloading tabs after calculation...');
      loadTabs();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler bei der Tab-Berechnung';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setCalculating(false);
    }
  };

  const handleCalculateAllPresets = async () => {
    setCalculating(true);
    setMessage('');

    try {
      const response = await axios.post('/api/tabs/calculate-all');
      
      if (response.data?.data?.tabs) {
        setTabs(response.data.data.tabs);
        setMessage(`${response.data.data.tabs.length} Tabs für alle Presets erfolgreich berechnet`);
        setMessageType('success');
      } else {
        setMessage(response.data?.message || 'Tabs für alle Presets berechnet');
        setMessageType('success');
      }
      
      loadTabs();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler bei der Tab-Berechnung für alle Presets';
      setMessage(errorMessage);
      setMessageType('error');
    } finally {
      setCalculating(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Alle berechneten Tabs löschen?')) {
      return;
    }

    try {
      const response = await axios.delete('/api/tabs/clear-all');
      setTabs([]);
      setMessage(response.data?.message || 'Alle Tabs gelöscht');
      setMessageType('success');
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen der Tabs';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleSendTab = async (tab) => {
    try {
      setSentTabs(prev => new Set([...prev, tab._id]));
      
      const response = await axios.post(`/api/tabs/${tab._id}/send`);
      setMessage(response.data?.message || 'Tab gesendet');
      setMessageType('success');
      
      // Tab in der Liste aktualisieren
      setTabs(prev => prev.map(t => 
        t._id === tab._id ? { ...t, sent: true } : t
      ));
      
    } catch (error) {
      setSentTabs(prev => {
        const newSet = new Set(prev);
        newSet.delete(tab._id);
        return newSet;
      });
      
      const errorMessage = error.response?.data?.message || 'Fehler beim Senden des Tabs';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleConfirmTab = async (tab) => {
    try {
      const response = await axios.post(`/api/tabs/${tab._id}/confirm`);
      setMessage(response.data?.message || 'Tab bestätigt');
      setMessageType('success');
      loadTabs();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Bestätigen des Tabs';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const handleDeleteTab = async (tab) => {
    try {
      const response = await axios.delete(`/api/tabs/${tab._id}`);
      setMessage(response.data?.message || 'Tab gelöscht');
      setMessageType('success');
      loadTabs();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Fehler beim Löschen des Tabs';
      setMessage(errorMessage);
      setMessageType('error');
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('de-DE');
  };

  const formatCoords = (coords) => {
    if (coords && typeof coords === 'object' && coords.x !== undefined && coords.y !== undefined) {
      return `${coords.x}|${coords.y}`;
    }
    return 'Unbekannt';
  };

  const getTabStatus = (tab) => {
    if (tab.confirmed) return { text: 'Bestätigt', class: 'bg-green-100 text-green-800' };
    if (tab.sent) return { text: 'Gesendet', class: 'bg-blue-100 text-blue-800' };
    if (new Date(tab.departure) < new Date()) return { text: 'Zu spät', class: 'bg-red-100 text-red-800' };
    return { text: 'Ausstehend', class: 'bg-yellow-100 text-yellow-800' };
  };

  const groupedTabs = groupBy === 'sos' ? 
    tabs.reduce((groups, tab) => {
      const key = tab.sosId || 'Ohne SOS';
      if (!groups[key]) groups[key] = [];
      groups[key].push(tab);
      return groups;
    }, {}) : 
    { 'Alle Tabs': tabs };

  const confirmedTabsCount = tabs.filter(tab => tab.confirmed).length;

  const renderTabs = () => {
    return (
      <div className="space-y-6">
        {Object.entries(groupedTabs).map(([groupKey, groupTabs]) => (
          <div key={groupKey}>
            {groupBy === 'sos' && (
              <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                <span className="text-2xl mr-3">🎯</span>
                {groupKey} ({groupTabs.length} Tabs)
              </h3>
            )}
            
            <div className="space-y-3">
              {groupTabs.map((tab) => {
                const status = getTabStatus(tab);
                const isSending = sentTabs.has(tab._id);
                const isSelected = selectedTabId === tab._id;
                
                return (
                  <div 
                    key={tab._id} 
                    onClick={() => setSelectedTabId(tab._id)}
                    className={`cursor-pointer transition-all duration-200 rounded-lg border p-4 hover:shadow-md ${
                      isSelected 
                        ? 'bg-blue-50 border-blue-300 shadow-md' 
                        : 'bg-white border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      {/* Tab Info */}
                      <div className="flex items-center space-x-6 flex-grow">
                        <div className="flex-shrink-0">
                          <div className="font-bold text-gray-800 text-lg">
                            Tab #{tab._id.slice(-6)}
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${status.class}`}>
                            {status.text}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 flex-grow text-sm">
                          <div>
                            <span className="text-gray-500">Von:</span>
                            <div className="coords text-xs mt-1">{formatCoords(tab.originCoords)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Nach:</span>
                            <div className="coords text-xs mt-1">{formatCoords(tab.targetCoords)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Abfahrt:</span>
                            <div className="text-gray-700 text-xs mt-1">{formatDateTime(tab.departure)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500 font-bold">Ankunft:</span>
                            <div className="text-red-600 font-bold text-xs mt-1">{formatDateTime(tab.arrival)}</div>
                          </div>
                          <div>
                            <span className="text-gray-500">Preset:</span>
                            <div className="text-gray-700 text-xs mt-1">{tab.presetName}</div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {tab.tabLink && (
                          <a
                            href={tab.tabLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-100 transition-colors"
                            title="Tab schicken"
                            onClick={(e) => e.stopPropagation()}
                          >
                            🔗
                          </a>
                        )}
                        
                        {!tab.confirmed && !tab.sent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSendTab(tab);
                            }}
                            disabled={isSending}
                            className="text-blue-600 hover:text-blue-800 p-2 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                            title="Tab schicken"
                          >
                            {isSending ? '⏳' : '�'}
                          </button>
                        )}
                        
                        {tab.sent && !tab.confirmed && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleConfirmTab(tab);
                            }}
                            className="text-green-600 hover:text-green-800 p-2 rounded hover:bg-green-100 transition-colors"
                            title="Tab bestätigen"
                          >
                            ✅
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTab(tab);
                          }}
                          className="text-red-600 hover:text-red-800 p-2 rounded hover:bg-red-100 transition-colors"
                          title="Tab löschen"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>

                    {/* Additional info when selected */}
                    {isSelected && (
                      <div className="mt-4 pt-4 border-t border-blue-200 bg-blue-25">
                        <div className="text-sm text-blue-800">
                          <strong>Ausgewählt:</strong> Klicken Sie "Tab schicken" um diesen Tab zu versendet, 
                          dann "Tab bestätigen" nach erfolgreichem Versand.
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        
        {/* Header */}
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-green-600 rounded-full shadow-lg mb-4">
            <span className="text-2xl text-white">📊</span>
          </div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">
            Tab-Berechnung
          </h1>
          <p className="text-xl text-gray-600">Intelligente Verteidigungsverteilung für Stammes-SOS</p>
        </div>

        {/* Status Message */}
        {message && (
          <div className={`px-6 py-4 rounded-lg border ${
            messageType === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
            messageType === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
            'bg-blue-50 border-blue-200 text-blue-700'
          }`}>
            <div className="font-medium">{message}</div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Preset Manager */}
          <div className="xl:col-span-1">
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                ⚙️ Presets verwalten
              </h2>
              <PresetManager 
                onPresetSelect={setSelectedPreset}
                onPresetsLoaded={setPresets}
                selectedPresetId={selectedPreset?._id}
              />
            </div>
          </div>

          {/* Tab Calculation Controls */}
          <div className="xl:col-span-2">
            <div className="card">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">
                🎯 Tab-Berechnung
              </h2>
              
              {/* Preset Selection */}
              <div className="mb-6">
                <label className="form-label">
                  Ausgewähltes Preset
                </label>
                {selectedPreset ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="font-semibold text-green-800 mb-2">{selectedPreset.name}</div>
                    <div className="text-green-600 text-sm">
                      Laufzeit: {selectedPreset.runtime}min | 
                      Einheiten: {Object.values(selectedPreset.units).reduce((sum, count) => sum + count, 0)}
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-gray-500">
                    Kein Preset ausgewählt
                  </div>
                )}
              </div>

              {/* Group By Options */}
              <div className="mb-6">
                <label className="form-label">
                  Gruppierung
                </label>
                <select 
                  value={groupBy} 
                  onChange={(e) => setGroupBy(e.target.value)}
                  className="form-input"
                >
                  <option value="sos">Nach SOS gruppieren</option>
                  <option value="none">Keine Gruppierung</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4 mb-6">
                {/* Single Preset Calculation */}
                <div>
                  <label className="form-label">
                    🎯 Einzelnes Preset berechnen
                  </label>
                  <p className="text-sm text-gray-600 mb-3">
                    Berechnet Tabs für das ausgewählte Preset basierend auf <strong>allen SOS-Incs des Stammes</strong>
                  </p>
                  {presets.length === 0 && (
                    <div className="mb-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center text-yellow-800">
                        <span className="mr-2">⚠️</span>
                        <span className="text-sm">
                          <strong>Keine Presets vorhanden!</strong> Erstellen Sie zuerst ein Preset links.
                        </span>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <button
                      onClick={handleCalculateAll}
                      disabled={calculating || !selectedPreset || presets.length === 0}
                      className={`btn-primary ${(presets.length === 0 || !selectedPreset) ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {calculating ? (
                        <span className="flex items-center justify-center">
                          <div className="spinner mr-2"></div>
                          Berechne...
                        </span>
                      ) : presets.length === 0 ? (
                        'Keine Presets vorhanden'
                      ) : !selectedPreset ? (
                        'Preset auswählen'
                      ) : (
                        `"${selectedPreset.name}" berechnen`
                      )}
                    </button>
                    
                    <button
                      onClick={handleClearAll}
                      disabled={tabs.length === 0}
                      className="btn-danger"
                    >
                      Alle Tabs löschen
                    </button>
                  </div>
                </div>

                {/* All Presets Calculation */}
                <div>
                  <label className="form-label">
                    🚀 Alle Presets berechnen
                  </label>
                  <div className="grid grid-cols-1 gap-4">
                    <button
                      onClick={handleCalculateAllPresets}
                      disabled={calculating || presets.length === 0}
                      className={`btn-success ${presets.length === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {calculating ? (
                        <span className="flex items-center justify-center">
                          <div className="spinner mr-2"></div>
                          Berechne alle...
                        </span>
                      ) : presets.length === 0 ? (
                        'Keine Presets vorhanden'
                      ) : (
                        `Alle ${presets.length} Presets berechnen`
                      )}
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    Berechnet automatisch Tabs für alle verfügbaren Presets basierend auf <strong>allen SOS-Incs des Stammes</strong>
                  </p>
                  {presets.length === 0 && (
                    <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center text-yellow-800">
                        <span className="mr-2">⚠️</span>
                        <span className="text-sm">
                          <strong>Keine Presets vorhanden!</strong> Erstellen Sie zuerst ein Preset links, um Tabs berechnen zu können.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-200">
                  <div className="text-2xl font-bold text-blue-800">{tabs.length}</div>
                  <div className="text-blue-600 text-sm">Tabs gesamt</div>
                </div>
                <div className="bg-green-50 rounded-lg p-4 text-center border border-green-200">
                  <div className="text-2xl font-bold text-green-800">{confirmedTabsCount}</div>
                  <div className="text-green-600 text-sm">Bestätigt</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Display */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              📋 Berechnete Tabs ({tabs.length})
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <div className="spinner mb-4 mx-auto"></div>
              <p className="text-gray-600">Lade Tabs...</p>
            </div>
          ) : tabs.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl text-gray-400">📊</span>
              </div>
              <p className="text-gray-500 text-lg mb-2">Keine Tabs berechnet</p>
              <p className="text-gray-400">Wählen Sie ein Preset und berechnen Sie Tabs</p>
            </div>
          ) : (
            renderTabs()
          )}
        </div>
      </div>
    </div>
  );
};

export default TabCalculation;
